// Pomocnicze, czyste funkcje edytora ciągłego (widok 1:1 wszystkich stron + edycja live in-place).
// Rozdzielone od PageViewerEditor.jsx, żeby komponent React został czytelny.

// Replika `worker._pdfgen_page_px_dims()` (jedyne źródło prawdy o fizycznych wymiarach strony w
// px @96dpi po stronie backendu, patrz plan) — DOKŁADNIE ta sama logika, żeby podgląd = przyszły
// PDF/PPTX 1:1 (nie tylko proporcje, ale te same piksele co Playwright).
export function pagePxDims(template) {
  let w, h
  if (((template && template.page_size) || '').toUpperCase() === '16:9') { w = 1280; h = 720 } else { w = 794; h = 1123 }
  const orientation = template && template.orientation
  if (orientation === 'landscape' && w === 794 && h === 1123) { w = 1123; h = 794 }
  else if (orientation === 'portrait' && w === 1280 && h === 720) { w = 720; h = 1280 }
  return [w, h]
}

const EDITABLE_KINDS = new Set(['heading', 'paragraph', 'quote', 'bullet_list', 'numbered_list'])

export function isEditableKind(kind) {
  return EDITABLE_KINDS.has(kind)
}

// Wstrzykuje do HTML strony wsparcie edycji live: klik w tekstowy slot -> contentEditable na
// miejscu (zamiast osobnego pola textarea); klik w obraz -> nakładka "Zmień zdjęcie"; śledzenie
// zaznaczenia (do pływającego mini-toolbara formatowania) i zdalne sterowanie execCommand z rodzica.
// Iframe ma sandbox="allow-scripts" BEZ allow-same-origin (patrz plan) — ten skrypt działa w
// odizolowanym, "opaque" originie: może tylko postMessage'ować do rodzica, nigdy nie ma dostępu do
// DOM/cookies/localStorage poza iframe'em, nawet gdyby treść slotu (html_values) była złośliwa.
export function injectEditingSupport(html, slotKinds) {
  const editableSlugs = Object.keys(slotKinds || {}).filter(s => isEditableKind(slotKinds[s]))
  const imageSlugs = Object.keys(slotKinds || {}).filter(s => slotKinds[s] === 'image')
  const payload = `
<style>
[data-content]{cursor:pointer}
[data-content]:hover{outline:2px dashed #1F5C52;outline-offset:1px}
[data-content][contenteditable="true"]{outline:2px solid #1F5C52;outline-offset:1px;cursor:text}
.gpdf-edit-hover{outline:2px dashed #1F5C52 !important;outline-offset:1px !important}
.gpdf-img-overlay{position:absolute;inset:0;background:rgba(21,63,56,.6);color:#fff;display:none;
  align-items:center;justify-content:center;font:600 13px sans-serif;cursor:pointer;text-align:center}
.gpdf-img-overlay-host:hover>.gpdf-img-overlay{display:flex}
.gpdf-img-overlay-host:hover{outline:2px dashed #1F5C52;outline-offset:1px}
</style>
<script>
(function(){
  var EDITABLE = ${JSON.stringify(editableSlugs)};
  var current = null;
  var hovered = null;
  function post(msg){ parent.postMessage(Object.assign({source:'gpdf-preview'}, msg), '*') }
  function fmtState(){
    try {
      return { bold: document.queryCommandState('bold'), italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'), ol: document.queryCommandState('insertOrderedList'),
        ul: document.queryCommandState('insertUnorderedList') }
    } catch (e) { return {} }
  }
  function rect(r){ return { top: r.top, left: r.left, width: r.width, height: r.height } }
  function flush(el){
    if (!el) return
    post({ type: 'content-changed', slot: el.getAttribute('data-content'), html: el.innerHTML, text: el.innerText })
  }
  function updateHover(e){
    var target = e.target && e.target.closest ? e.target.closest('[data-content],.gpdf-img-overlay-host') : null
    if (target && target.tagName === 'IMG' && target.parentElement.classList.contains('gpdf-img-overlay-host')) target = target.parentElement
    if (target === hovered) return
    if (hovered) hovered.classList.remove('gpdf-edit-hover')
    hovered = target
    if (hovered) hovered.classList.add('gpdf-edit-hover')
  }
  document.addEventListener('pointerover', updateHover, true)
  document.addEventListener('mouseover', updateHover, true)
  document.addEventListener('mousemove', updateHover, true)
  document.addEventListener('pointerleave', function(){
    if (hovered) hovered.classList.remove('gpdf-edit-hover')
    hovered = null
  }, true)
  document.addEventListener('click', function(e){
    var overlay = e.target.closest('.gpdf-img-overlay')
    if (overlay) { post({ type: 'image-click', slot: overlay.getAttribute('data-slot') }); return }
    var el = e.target.closest('[data-content]')
    if (!el) {
      if (current) flush(current)
      post({ type: 'preview-outside' })
      return
    }
    var slug = el.getAttribute('data-content')
    var er = el.getBoundingClientRect()
    var truncated = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1 ||
      getComputedStyle(el).textOverflow === 'ellipsis'
    post({ type: 'click', slot: slug, truncated: truncated, rect: rect(er), html: el.innerHTML, text: el.innerText })
    if (EDITABLE.indexOf(slug) === -1) return
    if (current && current !== el) flush(current)
    if (el.getAttribute('contenteditable') !== 'true') { el.setAttribute('contenteditable', 'true') }
    current = el
  }, true)
  document.addEventListener('selectionchange', function(){
    if (!current) return
    var sel = document.getSelection()
    if (!sel || sel.rangeCount === 0 || !current.contains(sel.anchorNode)) return
    var r = sel.getRangeAt(0).getBoundingClientRect()
    if (!r.width && !r.height) r = current.getBoundingClientRect()
    post({ type: 'selection', slot: current.getAttribute('data-content'), rect: rect(r), format: fmtState() })
  })
  var debounceTimer
  document.addEventListener('input', function(e){
    var el = e.target && e.target.closest ? e.target.closest('[data-content][contenteditable="true"]') : null
    if (!el) return
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(function(){ flush(el) }, 500)
  })
  document.addEventListener('focusout', function(e){
    var el = e.target && e.target.closest ? e.target.closest('[data-content][contenteditable="true"]') : null
    if (!el) return
    clearTimeout(debounceTimer)
    flush(el)
    post({ type: 'blur', slot: el.getAttribute('data-content') })
  })
  window.addEventListener('message', function(e){
    var d = e.data || {}
    if (d.type === 'exec') {
      if (current) current.focus()
      document.execCommand(d.cmd, false, d.value)
      if (current) flush(current)
    } else if (d.type === 'focus-slot') {
      var el = document.querySelector('[data-content="' + d.slot + '"]')
      if (!el) return
      el.scrollIntoView({ block: 'center' })
      if (EDITABLE.indexOf(d.slot) !== -1) { el.setAttribute('contenteditable', 'true'); current = el; el.focus() }
    } else if (d.type === 'end-editing') {
      if (current) {
        flush(current)
        current.removeAttribute('contenteditable')
      }
      current = null
      try { document.getSelection().removeAllRanges() } catch (ignore) {}
    }
  })
  ;(${JSON.stringify(imageSlugs)}).forEach(function(slug){
    var el = document.querySelector('[data-content="' + slug + '"]')
    if (!el) return
    var overlay = document.createElement('div')
    overlay.className = 'gpdf-img-overlay'
    overlay.setAttribute('data-slot', slug)
    overlay.textContent = 'Zmień zdjęcie'
    if (el.tagName === 'IMG') {
      // Nie owijamy IMG dodatkowym inline-blockiem: taki wrapper zmieniał układ procentowych
      // width/height i object-fit, więc grafika w edytorze miała inne proporcje niż w PDF.
      // Nakładka korzysta z istniejącego kontenera szablonu i nie wpływa na geometrię obrazu.
      var host = el.parentNode
      if (!host) return
      host.classList.add('gpdf-img-overlay-host')
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative'
      host.appendChild(overlay)
    } else {
      el.classList.add('gpdf-img-overlay-host')
      if (getComputedStyle(el).position === 'static') el.style.position = 'relative'
      el.appendChild(overlay)
    }
  })
})();
</script>`
  return /<\/body>/i.test(html) ? html.replace(/<\/body>/i, payload + '</body>') : html + payload
}

export function fitWidthZoom(viewerWidth, pageW) {
  return viewerWidth > 0 && pageW > 0 ? viewerWidth / pageW : 1
}

export function fitHeightZoom(viewerWidth, viewerHeight, pageW, pageH) {
  const fw = fitWidthZoom(viewerWidth, pageW)
  const fh = viewerHeight > 0 && pageH > 0 ? viewerHeight / pageH : 1
  return Math.min(fw, fh)
}
